/**
 * Email-draft review card (Automations Phase 4 — email responder go-live).
 *
 * Renders one drafted email reply awaiting sign-off: recipient, subject, and an
 * editable body. Send approves it as-is; editing the body and choosing "Save &
 * send" records an edited decision (the patched payload); Discard rejects it.
 * Lives under `features/activities` and talks to the approvals API only through
 * the `useEmailApprovals` hooks (no `modules` import — features may not reach up
 * into modules).
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AssistantMarkdown } from '@/common/components/AssistantMarkdown';
import { queryKeys } from '@/lib/query/keys';
import { useReviseSession } from '@/app/providers/ReviseSessionContext';
import { useConversation } from '@/app/providers/ConversationContext';
import { useChatDock } from '@/app/providers/ChatDockContext';
import { useSession } from '@/features/auth/hooks/useSession';
import { resolveWorkspaceId } from '@/lib/workspace-routing';
import { useDecideEmailApproval, type EmailDraftApproval } from './useEmailApprovals';
import '@/common/components/assistant-markdown.css';
import './email-draft-review.css';

/** Human-readable novelty flags — these are the "review this carefully" signals. */
const NOVELTY_LABEL: Record<string, string> = {
  new_external_recipient: 'New recipient — not someone you usually email',
  new_recipient: 'New recipient — not someone you usually email',
  large_send: 'Unusually large send',
  untrusted_input: 'Drafted from untrusted content',
};

/** Surface WHERE the recipient came from — a hijack shows up here. */
function provenanceNote(p: Record<string, unknown>): string | null {
  const from = typeof p.recipientFrom === 'string' ? p.recipientFrom : undefined;
  if (from === 'email_read' || from === 'observed') {
    return 'The recipient was taken from content the automation read — not from your instruction.';
  }
  return null;
}

export function EmailDraftReviewCard({ approval }: { approval: EmailDraftApproval }) {
  const decide = useDecideEmailApproval();
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(approval.body);
  const busy = decide.isPending;
  const prov = provenanceNote(approval.provenance);
  const bodyId = `email-draft-body-${approval.id}`;

  const { beginRevise, endRevise, reviseActive, activeApprovalId, isReviseConversationActive } = useReviseSession();
  // End the revise session only on a SUCCESSFUL terminal decision — tearing it
  // down before the mutation settles collapsed the 50/50 revise split even when
  // the Send/Discard failed (and gave no feedback). Runs from the mutation's
  // onSuccess; the MainLayout reconciler is still the safety net for other paths.
  //
  // Read the revise state through a ref so success-time reflects the CURRENT
  // session, not the click-time closure: if the user starts revising a DIFFERENT
  // draft while this Send is in flight, this card's onSuccess must not tear down
  // that newly-begun session.
  const reviseStateRef = useRef({ reviseActive, activeApprovalId });
  reviseStateRef.current = { reviseActive, activeApprovalId };
  const endReviseIfActive = () => {
    const { reviseActive: active, activeApprovalId: currentId } = reviseStateRef.current;
    if (active && currentId === approval.id) endRevise();
  };
  const { createConversation } = useConversation();
  const { expandChatPane } = useChatDock();
  const { selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const qc = useQueryClient();

  // While THIS draft is being revised, the tool writes the new body server-side; refresh the
  // approvals query on a short cadence so the card reflects it in place within a couple seconds.
  useEffect(() => {
    if (!reviseActive || activeApprovalId !== approval.id) return;
    const t = setInterval(() => {
      qc.invalidateQueries({ queryKey: queryKeys.agentTrust.approvals(workspaceId) });
    }, 3000);
    return () => clearInterval(t);
  }, [reviseActive, activeApprovalId, approval.id, workspaceId, qc]);

  const openRevise = () => {
    // Strict single-flight (§7.5, concurrency-review root fix): while a revise session is already
    // active — for ANY draft, including a re-click on THIS one before its own create resolves —
    // beginRevise is a clean no-op (checked SYNCHRONOUSLY against a ref inside ReviseSessionContext,
    // so this catches even a double-click that fires before the now-disabled button below re-renders).
    // A second attempt must not create a second chat, dispatch a second seed, or touch the live
    // session in any way — the user closes the current revise (Send/Save & send/Discard) before a new
    // one can begin.
    const tempId = `conversation-${Date.now()}`;
    const started = beginRevise(approval.id, tempId);
    if (!started) return;
    createConversation(
      { id: tempId, name: 'Revise draft', icon: '✏️', modules: [] },
      {
        preserveModuleId: null,
        // Evaluated at RESOLVE time, which may be long after the user has moved on (e.g. they closed
        // THIS session — Send/Save & send/Discard, or the reconciler's close/switch backstop — and
        // started a different one in the meantime): only activate/navigate if `tempId` is STILL the
        // conversation the current revise session is tracking. A stale resolve for an abandoned
        // session must not hijack the view or reactivate a dead conversation nobody is looking at.
        shouldActivateOnResolve: () => isReviseConversationActive(tempId),
      },
    );
    expandChatPane();
    // The seed listener turns this into ONE persisted assistant turn once the real conversation id
    // settles — carrying the current draft + a hidden revise-target(approvalId) marker so
    // `revise_email_draft` targets THIS draft (approvalId as a tool param, not a scope widen).
    window.dispatchEvent(new CustomEvent('xavior:revise-seed', {
      detail: { approvalId: approval.id, tempId, to: approval.to, subject: approval.subject, body: approval.body },
    }));
  };

  return (
    <div className="edr-card" role="group" aria-label="Drafted email reply">
      <div className="edr-card__hint">Drafted reply — review before it sends</div>

      {approval.noveltyFlags.length > 0 && (
        <div className="edr-card__warn" role="note">
          {approval.noveltyFlags.map((f) => (
            <span key={f} className="edr-card__warn-line">
              ⚠ {NOVELTY_LABEL[f] ?? f}
            </span>
          ))}
        </div>
      )}

      {approval.incomingSummary && (
        <div className="edr-card__summary" role="note">
          <span className="edr-card__summary-label">Replying to</span>
          <p className="edr-card__summary-text">{approval.incomingSummary}</p>
        </div>
      )}

      <dl className="edr-card__meta">
        <div className="edr-card__row">
          <dt className="edr-card__k">To</dt>
          <dd className="edr-card__v">{approval.to || '—'}</dd>
        </div>
        <div className="edr-card__row">
          <dt className="edr-card__k">Subject</dt>
          <dd className="edr-card__v">{approval.subject || '—'}</dd>
        </div>
      </dl>

      {editing ? (
        <label className="edr-card__body-label" htmlFor={bodyId}>
          <span className="edr-card__k">Body</span>
          <textarea
            id={bodyId}
            className="edr-card__body-edit"
            value={body}
            rows={6}
            disabled={busy}
            onChange={(e) => setBody(e.target.value)}
          />
        </label>
      ) : (
        <div className="edr-card__body" aria-label="Email body">
          <AssistantMarkdown content={approval.body || '—'} />
        </div>
      )}

      {approval.notes && (
        <div className="edr-card__notes" role="note">
          <span className="edr-card__notes-label">
            Note from Xavior — only you see this, it isn’t sent
          </span>
          <p className="edr-card__notes-text">{approval.notes}</p>
        </div>
      )}

      {prov && <p className="edr-card__prov">{prov}</p>}

      <div className="edr-card__actions">
        {editing ? (
          <>
            <button
              type="button"
              className="edr-btn edr-btn--primary"
              disabled={busy}
              onClick={() => {
                // Save & send is a terminal decision. On SUCCESS, close any revise
                // session on THIS draft so the app returns to strict single-open
                // (the card unmounts once it decides; the MainLayout reconciler is the
                // safety net for other terminal paths). Mirrors Discard.
                decide.mutate(
                  {
                    id: approval.id,
                    decision: 'edited',
                    preparedPayload: { ...approval.preparedPayload, body },
                  },
                  {
                    onSuccess: () => {
                      setEditing(false);
                      endReviseIfActive();
                    },
                  },
                );
              }}
            >
              Save &amp; send
            </button>
            <button
              type="button"
              className="edr-btn"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setBody(approval.body);
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="edr-btn edr-btn--primary"
              disabled={busy}
              onClick={() => {
                // Send is a terminal decision — restore strict single-open only once
                // it succeeds (the card unmounts on success; the MainLayout reconciler
                // backs it up for other paths). Mirrors Discard.
                decide.mutate(
                  { id: approval.id, decision: 'approved' },
                  { onSuccess: endReviseIfActive },
                );
              }}
            >
              Send
            </button>
            <button
              type="button"
              className="edr-btn"
              disabled={busy}
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
            <button
              type="button"
              className="edr-btn"
              // Gate on `reviseActive` (not just this card's own `busy`) — a revise session, on ANY
              // draft, spans the entire create round-trip through to close, so this disables every
              // card's Revise button for the whole time one is in flight or open (strict single-flight,
              // §7.5 concurrency-review root fix). `openRevise`'s beginRevise call is ALSO reentrancy-
              // safe against a same-tick double-click that fires before this disabled attribute
              // re-renders — this is the visible/UX half of that guard, not the only one.
              disabled={busy || reviseActive}
              onClick={openRevise}
            >
              Revise
            </button>
          </>
        )}
        <button
          type="button"
          className="edr-btn edr-btn--danger"
          disabled={busy}
          onClick={() => {
            decide.mutate(
              { id: approval.id, decision: 'rejected' },
              { onSuccess: endReviseIfActive },
            );
          }}
        >
          Discard
        </button>
      </div>

      {decide.isError && (
        <p className="edr-card__error" role="alert">
          That didn’t go through — check your connection and try again.
        </p>
      )}
    </div>
  );
}
